import { useMemo, useState } from 'react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@artstorefronts/ui';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SearchablePoint {
  userId: string;
  displayName: string | null;
}

interface ArtistSearchProps {
  points: SearchablePoint[];
  selectedUserId: string | null;
  onSelect: (userId: string) => void;
}

const MAX_RESULTS = 50;

function ArtistSearch({ points, selectedUserId, onSelect }: ArtistSearchProps) {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedLabel = useMemo(() => {
    if (!selectedUserId) return null;
    const match = points.find((p) => p.userId === selectedUserId);
    return match?.displayName ?? selectedUserId;
  }, [points, selectedUserId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hasName = points.filter((p) => p.displayName);
    if (!q) return hasName.slice(0, MAX_RESULTS);
    return hasName.filter((p) => (p.displayName ?? '').toLowerCase().includes(q)).slice(0, MAX_RESULTS);
  }, [points, query]);

  const handleSelect = (userId: string) => {
    onSelect(userId);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          size="sm"
          className="h-8 w-56 justify-start text-xs font-normal text-zinc-600"
        >
          <Search className="mr-2 size-3.5 shrink-0 opacity-60" />
          <span className="truncate">{selectedLabel ?? t('artist-genome.search-placeholder')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={t('artist-genome.search-placeholder')} value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>{t('artist-genome.search-no-results')}</CommandEmpty>
            <CommandGroup>
              {filtered.map((p) => (
                <CommandItem
                  key={p.userId}
                  value={p.userId}
                  onSelect={() => handleSelect(p.userId)}
                  className="text-xs"
                >
                  {p.displayName}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ArtistSearch;
